import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Clock, DollarSign, User, Check, X, CalendarClock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useState } from "react";
import { format } from "date-fns";
import { formatInTimeZone, toZonedTime } from "date-fns-tz";
import { formatTime } from "@/lib/utils";
import { CoachFeedbackDialog } from "./CoachFeedbackDialog";

interface Booking {
  id: string;
  booking_datetime: string; // UTC ISO timestamp
  booking_timezone?: string;
  duration_minutes: number;
  status: string;
  price: number;
  user_id: string;
  coach_id?: string;
  proposed_datetime?: string; // UTC ISO timestamp for proposed time
  video_conference_link?: string;
  denied_by?: string[];
  coach_feedback?: string;
  profiles?: {
    full_name: string;
    email: string;
  };
}

interface BookingRequestCardProps {
  booking: Booking;
  showActions?: boolean;
  onUpdate?: () => void;
  overrideCoachId?: string; // For admin view - deny on behalf of this coach
}

export const BookingRequestCard = ({ booking, showActions = false, onUpdate, overrideCoachId }: BookingRequestCardProps) => {
  const [showProposeDialog, setShowProposeDialog] = useState(false);
  const [proposedDate, setProposedDate] = useState<Date | undefined>(new Date());
  const [proposedTime, setProposedTime] = useState<string>("");

  const timeSlots = [
    { value: "09:00", label: "9:00 AM" },
    { value: "10:00", label: "10:00 AM" },
    { value: "11:00", label: "11:00 AM" },
    { value: "12:00", label: "12:00 PM" },
    { value: "13:00", label: "1:00 PM" },
    { value: "14:00", label: "2:00 PM" },
    { value: "15:00", label: "3:00 PM" },
    { value: "16:00", label: "4:00 PM" },
    { value: "17:00", label: "5:00 PM" },
  ];

  const sendNotificationEmail = async (status: string, proposedDatetimeUTC?: string) => {
    try {
      // Get coach profile - use overrideCoachId if admin is acting on behalf of coach
      const { data: { user } } = await supabase.auth.getUser();
      let coachName = undefined;
      
      if (user) {
        // Determine which coach ID to use for fetching the profile
        const coachIdToFetch = overrideCoachId || booking.coach_id || user.id;
        
        const { data: coachProfile } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", coachIdToFetch)
          .single();
        coachName = coachProfile?.full_name || undefined;
      }

      // Get user's timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      await supabase.functions.invoke('send-booking-notification', {
        body: {
          customerEmail: booking.profiles?.email,
          customerName: booking.profiles?.full_name || "Customer",
          bookingDatetime: booking.booking_datetime,
          duration: booking.duration_minutes,
          status: status,
          proposedDatetime: proposedDatetimeUTC,
          bookingId: booking.id,
          coachName: coachName,
          timezone: booking.booking_timezone || userTimezone,
          videoConferenceLink: booking.video_conference_link,
        },
      });
    } catch (error) {
      console.error("Error sending email:", error);
      // Don't block the action if email fails
    }
  };

  const handleStatusUpdate = async (newStatus: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("You must be logged in");
        return;
      }

      // If denying, add coach to denied_by array
      if (newStatus === "denied") {
        // Use overrideCoachId if provided (admin view), otherwise use current user
        const coachIdToAdd = overrideCoachId || user.id;
        const currentDeniedBy = booking.denied_by || [];
        
        // Check if this coach has already denied - if so, don't add again
        if (currentDeniedBy.includes(coachIdToAdd)) {
          toast.error("This coach has already denied this booking");
          onUpdate?.();
          return;
        }
        
        const updatedDeniedBy = [...currentDeniedBy, coachIdToAdd];
        
        // Get all current coach IDs
        const { data: currentCoaches, error: coachError } = await supabase
          .from("user_roles")
          .select("user_id")
          .eq("role", "coach");

        if (coachError) throw coachError;
        
        const currentCoachIds = new Set(currentCoaches?.map(c => c.user_id) || []);
        const totalCoachCount = currentCoachIds.size;
        
        // Count how many CURRENT coaches have denied (filter out old/invalid coach IDs)
        const currentCoachesWhoDenied = updatedDeniedBy.filter(id => currentCoachIds.has(id));
        const uniqueCurrentCoachDenials = new Set(currentCoachesWhoDenied).size;
        
        console.log(`Coach denied booking. Total current coaches: ${totalCoachCount}, Current coaches who denied: ${uniqueCurrentCoachDenials}`);

        // If all CURRENT coaches have denied, update status to denied and cancel payment
        const allCoachesDenied = totalCoachCount > 0 && uniqueCurrentCoachDenials >= totalCoachCount;
        
        const updateData: any = { denied_by: updatedDeniedBy };
        if (allCoachesDenied) {
          updateData.status = "denied";
          console.log("All coaches have denied - updating status to denied");
        }

        const { error } = await supabase
          .from("bookings")
          .update(updateData)
          .eq("id", booking.id);

        if (error) throw error;

        // If all coaches denied, cancel the payment
        if (allCoachesDenied) {
          try {
            console.log("Canceling payment for fully denied booking");
            await supabase.functions.invoke('cancel-payment', {
              body: { bookingId: booking.id }
            });
            toast.success("Booking denied by all coaches - payment has been canceled");
          } catch (cancelError) {
            console.error("Error canceling payment:", cancelError);
            toast.error("Booking denied but payment cancellation failed");
          }
        } else {
          toast.success("Booking denied - other coaches can still accept this booking");
        }
        
        onUpdate?.();
        return;
      }

      const updateData: any = { status: newStatus };
      
      // If accepting, assign the booking to this coach and capture payment
      if (newStatus === "confirmed") {
        // Check if booking has already passed
        const bookingDateTime = new Date(booking.booking_datetime);
        const now = new Date();
        
        if (bookingDateTime < now) {
          toast.error("Cannot accept a booking that has already passed");
          onUpdate?.();
          return;
        }

        updateData.coach_id = user.id;

        // Generate unique video conference link using Jitsi Meet
        const roomName = `vocalzing-${booking.id.substring(0, 8)}`;
        updateData.video_conference_link = `https://meet.jit.si/${roomName}#config.prejoinConfig.enabled=false&config.requireDisplayName=false`;

        // Capture (approve) the payment - this charges the customer
        console.log("Capturing payment for booking:", booking.id);
        const { data: { session } } = await supabase.auth.getSession();
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/capture-payment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ bookingId: booking.id }),
        });

        if (!response.ok) {
          const result = await response.json();
          console.error("Payment capture failed:", result);
          throw new Error(result.error || 'Failed to capture payment');
        }

        console.log("Payment captured successfully for booking:", booking.id);

        // Use overrideCoachId if provided (admin accepting on behalf of coach)
        const coachIdToAssign = overrideCoachId || user.id;
        updateData.coach_id = coachIdToAssign;

        // Prevent race conditions: only update if booking is still pending
        // Allow if coach_id is null (unassigned) OR matches the coach being assigned (pre-assigned single coach scenario)
        const { data, error } = await supabase
          .from("bookings")
          .update(updateData)
          .eq("id", booking.id)
          .eq("status", "pending")
          .or(`coach_id.is.null,coach_id.eq.${coachIdToAssign}`)
          .select();

        if (error) throw error;

        // Check if the update actually happened (no rows means booking was already accepted by someone else)
        if (!data || data.length === 0) {
          toast.error("This booking has already been accepted by another coach");
          onUpdate?.();
          return;
        }

        await sendNotificationEmail(newStatus);
        toast.success("Booking accepted! Payment has been captured.");
        onUpdate?.();
      }
    } catch (error) {
      console.error("Error updating booking:", error);
      toast.error("Failed to update booking: " + (error instanceof Error ? error.message : "Unknown error"));
    }
  };

  const handleProposeTime = async () => {
    if (!proposedDate || !proposedTime) {
      toast.error("Please select both date and time");
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("You must be logged in");
        return;
      }

      // Convert proposed time to UTC
      // The proposedTime from the select is in the format "HH:mm"
      // We need to create a proper datetime in the user's timezone
      const userTimezone = booking.booking_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const proposedDateStr = format(proposedDate, "yyyy-MM-dd");
      
      // Parse as local date/time in the user's timezone, then convert to UTC
      const localDateTimeStr = `${proposedDateStr}T${proposedTime}:00`;
      const zonedDate = toZonedTime(localDateTimeStr, userTimezone);
      const proposedDatetimeUTC = zonedDate.toISOString();

      const { error } = await supabase
        .from("bookings")
        .update({ 
          proposed_datetime: proposedDatetimeUTC,
          proposed_by: user.id,
          status: "proposed"
        })
        .eq("id", booking.id);

      if (error) throw error;

      await sendNotificationEmail("proposed", proposedDatetimeUTC);
      toast.success("Alternative time proposed successfully!");
      setShowProposeDialog(false);
      onUpdate?.();
    } catch (error) {
      console.error("Error proposing time:", error);
      toast.error("Failed to propose alternative time");
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-6">
        <div className="space-y-3">
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="font-semibold">
                  {booking.profiles?.full_name || "Unknown"}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {booking.profiles?.email}
              </p>
            </div>
            <Badge
              variant={
                booking.status === "confirmed"
                  ? "default"
                  : booking.status === "cancelled"
                  ? "destructive"
                  : "secondary"
              }
            >
              {booking.status}
            </Badge>
          </div>

            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <CalendarClock className="h-4 w-4" />
                <span>
                  {formatInTimeZone(
                    new Date(booking.booking_datetime), 
                    booking.booking_timezone || 'America/New_York', 
                    'MMM d, yyyy'
                  )}
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>
                  {formatInTimeZone(
                    new Date(booking.booking_datetime),
                    booking.booking_timezone || 'America/New_York',
                    'h:mm a'
                  )} ({booking.duration_minutes} min)
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <DollarSign className="h-4 w-4" />
                <span>${booking.price}</span>
              </div>
            </div>

          {booking.status === "confirmed" && booking.video_conference_link && (
            <div className="bg-primary/10 p-3 rounded-lg border border-primary/20">
              <p className="text-xs text-muted-foreground mb-1">Video Conference Link:</p>
              <a 
                href={booking.video_conference_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm font-medium text-primary hover:underline break-all"
              >
                {booking.video_conference_link}
              </a>
            </div>
          )}

          {booking.status === "confirmed" && showActions && (
            <div className="pt-2">
              <CoachFeedbackDialog 
                bookingId={booking.id}
                existingFeedback={booking.coach_feedback}
                onFeedbackSaved={onUpdate}
              />
            </div>
          )}

          {showActions && booking.status === "pending" && (
            <div className="flex gap-2 pt-2">
              <Button
                size="sm"
                variant="default"
                onClick={() => handleStatusUpdate("confirmed")}
                className="flex-1"
              >
                <Check className="h-4 w-4 mr-1" />
                Accept
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleStatusUpdate("denied")}
                className="flex-1"
              >
                <X className="h-4 w-4 mr-1" />
                Deny
              </Button>
              <Dialog open={showProposeDialog} onOpenChange={setShowProposeDialog}>
                <DialogTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                  >
                    <CalendarClock className="h-4 w-4 mr-1" />
                    Propose Time
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Propose Alternative Time</DialogTitle>
                    <DialogDescription>
                      Suggest a different date and time for this booking
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-2">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Select Date</label>
                      <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl p-4 border border-primary/10 shadow-inner">
                        <div className="flex justify-center">
                          <Calendar
                            mode="single"
                            selected={proposedDate}
                            onSelect={setProposedDate}
                            disabled={(date) => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              const checkDate = new Date(date);
                              checkDate.setHours(0, 0, 0, 0);
                              return checkDate < today;
                            }}
                            className="pointer-events-auto rounded-lg border-0"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium flex items-center gap-2">
                        <Clock className="w-4 h-4 text-primary" />
                        Select Time (your local timezone)
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {timeSlots.map((slot) => (
                          <Button
                            key={slot.value}
                            variant={proposedTime === slot.value ? "default" : "outline"}
                            size="sm"
                            onClick={() => setProposedTime(slot.value)}
                            className="transition-all hover:scale-105"
                          >
                            {slot.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <Button 
                      onClick={handleProposeTime}
                      variant="hero"
                      size="lg"
                      className="w-full mt-4"
                      disabled={!proposedDate || !proposedTime}
                    >
                      <CalendarClock className="w-4 h-4 mr-2" />
                      Propose This Time
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
